import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getSearchData } from '../features/lobby/lobbySlice';
import MainLoader from '../components/Structures/MainLoader';
import LobbySearchBox from '../components/lobby/LobbySearchBox';
import EngagementsTable from '../components/lobby/EngagementsTable';
import { formatDate, lobbyistName, StatusBadge } from '../components/lobby/lobbyFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, StickyBar } from '../components/ui';

const CHANGE_FILTERS = [
  { id: 'all', label: 'All changes' },
  { id: 'Active', label: 'New or updated' },
  { id: 'Ended', label: 'Ended' },
];

const Stat = ({ label, value, raw = false }) => (
  <div className="min-w-[140px] flex-1 border-b border-r border-border/60 px-5 py-4">
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="mt-1 text-2xl font-semibold tracking-tight tabular-nums text-foreground">
      {value == null ? '—' : raw ? value : Number(value).toLocaleString()}
    </dd>
  </div>
);

const LobbySearch = () => {
  const dispatch = useDispatch();
  const { state } = useParams();
  const { lobby, isLoading, isError } = useSelector((s) => s.lobby);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    dispatch(getSearchData());
  }, [dispatch, state]);

  const changes = lobby?.changes;
  const rows = useMemo(() => {
    const all = changes?.rows || [];
    return filter === 'all' ? all : all.filter((r) => r.LobbyEngagementStatus === filter);
  }, [changes, filter]);

  const columns = [
    { key: 'UpdatedAt', label: 'Updated', className: 'whitespace-nowrap', render: (r) => formatDate(r.UpdatedAt), sortValue: (r) => r.UpdatedAt },
    {
      key: 'LobbyistLastName',
      label: 'Lobbyist',
      render: (r) => (
        <Link to={`/${state}/lobbyist/${r.LobbyistID}`} className="font-medium text-info hover:text-info-strong">
          {lobbyistName(r)}
        </Link>
      ),
    },
    {
      key: 'CompanyName',
      label: 'Client',
      render: (r) => (
        <Link to={`/${state}/company/${r.CompanyID}`} className="font-medium text-info hover:text-info-strong">
          {r.CompanyName}
        </Link>
      ),
    },
    { key: 'LobbyEngagementStatus', label: 'Status', render: (r) => <StatusBadge status={r.LobbyEngagementStatus} /> },
    { key: 'BeginDate', label: 'Begins', className: 'whitespace-nowrap', render: (r) => formatDate(r.BeginDate) },
    { key: 'StopDate', label: 'Ends', className: 'whitespace-nowrap', render: (r) => formatDate(r.StopDate) },
  ];

  return (
    <div className="mx-auto w-full max-w-[1600px] space-y-5">
      {/* Pinned just below the fixed top bar so search is always reachable. */}
      <StickyBar className="rounded-lg border border-border bg-card px-4 py-3.5 shadow-sm">
        <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <h1 className="text-xl font-semibold tracking-tight text-foreground">Lobby</h1>
          <p className="hidden text-sm text-muted-foreground sm:block">
            Registered lobbyists and the clients they represent in {state?.toUpperCase()}.
          </p>
        </div>
        <LobbySearchBox autoFocus />
      </StickyBar>

      {isLoading && <MainLoader label="Loading lobby data…" />}
      {isError && !isLoading && (
        <Card>
          <CardContent className="py-8 text-center text-sm text-danger">Couldn't load lobby data. Try refreshing.</CardContent>
        </Card>
      )}

      {!isLoading && !isError && lobby?.stats && (
        <>
          <div className="overflow-hidden rounded-md border border-border bg-card">
            <dl className="-mb-px -mr-px flex flex-wrap">
              <Stat label="Registered lobbyists" value={lobby.stats.TotalLobbyists} />
              <Stat label="Clients represented" value={lobby.stats.TotalCompanies} />
              <Stat label="Active engagements" value={lobby.stats.ActiveEngagements} />
              <Stat label="Latest registration year" value={lobby.stats.LatestYear} raw />
            </dl>
          </div>

          <Card>
            <CardHeader className="gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div className="space-y-1.5">
                <CardTitle>Recent changes</CardTitle>
                <CardDescription>
                  Engagements added or updated in the last {changes?.days ?? 30} days
                  {changes?.rows?.length >= changes?.limit ? ` (showing the latest ${changes.limit})` : ''}.
                </CardDescription>
              </div>
              <div className="inline-flex h-9 rounded-md bg-muted p-1 text-muted-foreground" role="tablist">
                {CHANGE_FILTERS.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    role="tab"
                    aria-selected={filter === f.id}
                    onClick={() => setFilter(f.id)}
                    className={`rounded-sm px-3 text-sm font-medium transition-colors ${
                      filter === f.id ? 'bg-background text-foreground shadow-sm' : 'hover:text-foreground'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </CardHeader>
            <CardContent>
              <EngagementsTable
                columns={columns}
                rows={rows}
                defaultSort={{ key: 'UpdatedAt', dir: 'desc' }}
                getRowKey={(r) => r.LobbyingEngagementID}
                emptyMessage="No lobbying changes recorded in this window."
              />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default LobbySearch;
